<template>
  <div class="work-time" v-if="workTime && options">
    <div class="head"><b>就業時間{{ (index + 1) }}</b></div>
    <div class="day flex">
      <div class="item">
        <p>曜日</p>
        <div class="check-weeks">
          <input-checkbox :options="[{ val: false, label: OptionConst.YOUBI_LABEL.MONDAY }]"
            v-model="workTime.is_monday" :disabled="disabled"></input-checkbox>
          <input-checkbox :options="[{ val: false, label: OptionConst.YOUBI_LABEL.TUESDAY }]"
            v-model="workTime.is_tuesday" :disabled="disabled"></input-checkbox>
          <input-checkbox :options="[{ val: false, label: OptionConst.YOUBI_LABEL.WEDNESDAY }]"
            v-model="workTime.is_wednesday" :disabled="disabled"></input-checkbox>
          <input-checkbox :options="[{ val: false, label: OptionConst.YOUBI_LABEL.THURSDAY }]"
            v-model="workTime.is_thursday" :disabled="disabled"></input-checkbox>
          <input-checkbox :options="[{ val: false, label: OptionConst.YOUBI_LABEL.FRIDAY }]"
            v-model="workTime.is_friday" :disabled="disabled"></input-checkbox>
          <input-checkbox :options="[{ val: false, label: OptionConst.YOUBI_LABEL.SATURDAY }]"
            v-model="workTime.is_saturday" :disabled="disabled"></input-checkbox>
          <input-checkbox :options="[{ val: false, label: OptionConst.YOUBI_LABEL.SUNDAY }]"
            v-model="workTime.is_sunday" :disabled="disabled"></input-checkbox>
        </div>
        <p class="input-error youbi-error" v-if="getError(workTime, 'youbis')">{{ getError(workTime, 'youbis') }}</p>
      </div>
    </div>

    <div class="week flex">
      <div class="item">
        <p>週（未指定の場合は「毎週」として扱われます）</p>
        <input-checkbox :options="OptionConst.WEEK_OF_NUM" v-model="workTime.weeks" :disabled="disabled"></input-checkbox>
      </div>
    </div>

    <div class="time flex">
      <div class="item">
        <p>開始</p>
        <time-picker :isClearable=false :minutesIncrement=options?.dakoku_round :startTime="{ minutes: 0 }"
          v-model="workTime.start_time" :error="getError(workTime, 'start_time')" :disabled="disabled"></time-picker>
      </div>
      <div class="item">
        <p>終了</p>
        <time-picker :isClearable=false :minutesIncrement=options?.dakoku_round :startTime="{ minutes: 0 }"
          v-model="workTime.end_time" :error="getError(workTime, 'end_time')" :disabled="disabled"></time-picker>
      </div>
      <div class="item">
        <p>通常勤務時間</p>
        <time-picker :isClearable=false :minutesIncrement=options?.dakoku_round v-model="workTime.tsuujou_times"
          :error="getError(workTime, 'tsuujou_times')" :disabled="disabled"></time-picker>
      </div>

      <div class="item">
        <p>休憩時間</p>
        <div class="flex flex-align-center">
          <time-picker :isClearable=false :minutesIncrement=options?.dakoku_round v-model="workTime.kyuukei_times"
            :error="getError(workTime, 'kyuukei_times')" :disabled="disabled"></time-picker>
          <p v-if="workTime.is_hannichi">※半日勤務日は休憩なし</p>
        </div>
      </div>
    </div>

    <div class="time flex">
      <div class="item">
        <p>半日勤務</p>
        <select-box :options="options?.boolean_type" v-model="workTime.is_hannichi" :disabled="!canSelectHannichi || disabled"
          @change="updateHannichi" class="small"></select-box>
      </div>
    </div>

    <div class="time flex" v-if="workTime.is_hannichi === false">
      <div class="item">
        <input-radio :options="OptionConst.KYUUJISU_SYUTOKU_TYPE_OPTIONS" v-model="selectedKyuujitsuSyutokuType"
          @update="updateKyuujitsuSyutokuType" :disabled="disabled"></input-radio>
      </div>
    </div>

    <div class="time flex" v-if="workTime.is_hannichi === false && selectedKyuujitsuSyutokuType === OptionConst.KYUUJISU_SYUTOKU_TYPE.NOT_ONE_DAY">
      <div class="item kyuujitsu-type">
        <input-radio :options="OptionConst.KINMU_TYPE_OPTIONS" v-model="selectedKinmuType"
          @update="updateKinmuType" :disabled="disabled"></input-radio>
      </div>
    </div>

    <div class="item row-button">
      <i v-if="canRemove" class="fa-solid fa-delete-left" @click="$emit('remove', index)"></i>
      <template v-if="canAdd && !disabled">
        <i class="fa-solid fa-circle-plus" @click="$emit('add')"></i>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { useForm } from '@/composable/form/form';
import { KoyouKeiyakuOption, KoyouKeiyakuTime } from '@/types/KoyouKeiyaku';
import { Ref, defineComponent, onMounted, ref } from 'vue';
import OptionConst from '@/const/Option';

export default defineComponent({
  props: {
    workTime: {
      type: Object as () => KoyouKeiyakuTime | null,
      require: true,
      default: null
    },
    index: {
      type: Number,
      require: true,
      default: 0,
    },
    options: {
      type: Object as () => KoyouKeiyakuOption | null,
      require: true,
      default: null,
    },
    canAdd: {
      type: Boolean,
      require: true,
      default: false,
    },
    canRemove: {
      type: Boolean,
      require: true,
      default: false,
    },
    disabled: {
      type: Object as () => boolean,
      default: false
    },
    mode: {
      // モード 新規新規(create)／既存契約からの新規作成(from)／編集(edit)／承認後の編集（modify）の４モードを想定
      type: String,
      require: true,
      default: null
    }
  },
  emits: {
    remove: (index: number) => true,
    add: () => true,
  },
  setup(props) {
    const { getError } = useForm()
    // １日有休を１日として扱うか否かの値が入る（制御用／リクエストには含まない）
    const selectedKyuujitsuSyutokuType: Ref<number> = ref(OptionConst.KYUUJISU_SYUTOKU_TYPE.ONE_DAY) 

    // １日有休を１日として扱わない場合に値が入る（制御用／リクエストには含まない）
    // 入る値としてはOptionConst.KINMU_TYPEのいずれかで、AM_KINMUならcan_only_am_kinmuもtrueにし、PM_KINMUならcan_only_pm_kinmuもtrueすること
    //   ※ OptionConst.KINMU_TYPEの値（１や２）がそのままテーブルに保存されるわけではない
    //   ※ この場合、is_hannichiは必ずfalseとなる＝１日勤務がなく常に半日勤務なので「半日勤務：有」とはならない。
    //      半日勤務フラグはあくまで「基本は１日勤務だけど半日勤務がある」ということを表す
    // いずれもfalseの場合はnullが入り、その場合はcan_only～はいずれもfalseとなる
    //   ※ この場合はis_hannichiの値に制約はない
    const selectedKinmuType: Ref<number | null> = ref(null) 

    // 半日勤務の選択肢の操作可否フラグ（can_only～の２フラグのいずれかがtrueの場合はfalseとする）
    const canSelectHannichi = ref(true) 

    onMounted(async () => {      
      if (!props.workTime) {
        return
      }

      if(props.workTime.can_only_am_kinmu || props.workTime.can_only_pm_kinmu){
        // can_only～のいずれかがtrueの場合
        props.workTime.is_hannichi = false
        canSelectHannichi.value = false
        selectedKyuujitsuSyutokuType.value = OptionConst.KYUUJISU_SYUTOKU_TYPE.NOT_ONE_DAY

        if(props.workTime.can_only_am_kinmu){
          selectedKinmuType.value = OptionConst.KINMU_TYPE.AM_KINMU
        } else if(props.workTime.can_only_pm_kinmu){
          selectedKinmuType.value = OptionConst.KINMU_TYPE.PM_KINMU
        }
      }
    })

    const updateHannichi = () => {
      if (!props.workTime) {
        return
      }

      if (props.workTime.is_hannichi === true) {
        canSelectHannichi.value = true
        selectedKinmuType.value = null
        selectedKyuujitsuSyutokuType.value = OptionConst.KYUUJISU_SYUTOKU_TYPE.ONE_DAY
        props.workTime.can_only_am_kinmu = false
        props.workTime.can_only_pm_kinmu = false
      }
    }

    const updateKyuujitsuSyutokuType = () => {
      if (!props.workTime) {
        return
      }

      if (selectedKyuujitsuSyutokuType.value === OptionConst.KYUUJISU_SYUTOKU_TYPE.ONE_DAY) {
        canSelectHannichi.value = true
        selectedKinmuType.value = null
        props.workTime.can_only_am_kinmu = false
        props.workTime.can_only_pm_kinmu = false
      } else {
        selectedKinmuType.value = OptionConst.KINMU_TYPE.AM_KINMU
        canSelectHannichi.value = false
        props.workTime.is_hannichi = false
        props.workTime.can_only_am_kinmu = true
        props.workTime.can_only_pm_kinmu = false
      }
    }

    const updateKinmuType = () => {
      if (!props.workTime) {
        return
      }
      switch (selectedKinmuType.value) {
        case OptionConst.KINMU_TYPE.AM_KINMU:
          props.workTime.is_hannichi = false
          canSelectHannichi.value = false
          props.workTime.can_only_am_kinmu = true
          props.workTime.can_only_pm_kinmu = false
          break;
        case OptionConst.KINMU_TYPE.PM_KINMU:
          props.workTime.is_hannichi = false
          canSelectHannichi.value = false
          props.workTime.can_only_am_kinmu = false
          props.workTime.can_only_pm_kinmu = true
          break;
        default:
          break;
      }
    }

    return {
      getError,
      OptionConst,
      selectedKyuujitsuSyutokuType,
      selectedKinmuType,
      updateKyuujitsuSyutokuType,
      updateKinmuType,
      canSelectHannichi,
      updateHannichi
    }
  }
})
</script>

<style lang="scss" scoped>
@import '@scss/components/koyoukeiyaku.scss';
@import '@scss/components/koyoukeiyaku-edit.scss';
@import '@scss/components/koyoukeiyaku-work_times.scss';
</style>
